import {
  Flex,
  Slider,
  Button,
  Checkbox,
  ChevronEndIcon,
} from "@fluentui/react-northstar";
import { Select, Typography, Row, Col } from "antd";
import { useAssignment } from "../../../../context/AssignmentContext";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import { useState } from "react";

const { Text } = Typography;
const { Option } = Select;

const AssignmentSettings = ({
  assignmentSettings,
  setAssignmentSettings,
  setSelectedTab,
}) => {
  const { currentAssignmentData, setCurrentAssignmentData } = useAssignment();
  const [isConfirmAiGrading, setIsConfirmAiGrading] = useState(false);

  const isRubricHasPoints =
    Object.values(currentAssignmentData?.rubric || {})?.length &&
    currentAssignmentData?.rubric?.rubricItems?.[0]?.havePoints;

  const handleAiGradingChange = (e, { checked }) => {
    if (checked) {
      // Show confirmation dialog when enabling AI Grading
      setIsConfirmAiGrading(true);
    } else {
      // Directly disable AI Grading
      setCurrentAssignmentData({
        ...currentAssignmentData,
        aiGrading: false,
        isChanged: true,
      });
    }
  };

  const handleConfirmAiGrading = () => {
    setCurrentAssignmentData({
      ...currentAssignmentData,
      aiGrading: true,
      isChanged: true,
    });
    setIsConfirmAiGrading(false);
  };

  const handleCancelAiGrading = () => {
    setIsConfirmAiGrading(false);
  };

  return (
    <div>
      <Row gutter={24}>
        <Col xs={24} sm={24} md={24} lg={10} xl={10}>
          <Flex gap="gap.small" style={{ marginBottom: 10 }} vAlign="center">
            <Checkbox
              data-cy={
                currentAssignmentData.aiGrading
                  ? "on-ai-grading-toggler-switch"
                  : "off-ai-grading-toggler-switch"
              }
              toggle
              label="AI Grading"
              checked={currentAssignmentData.aiGrading}
              disabled={
                !Object.values(currentAssignmentData?.rubric || {}).length ||
                !isRubricHasPoints
              }
              onChange={handleAiGradingChange}
            />
            {(!Object.values(currentAssignmentData?.rubric || {}).length ||
              !isRubricHasPoints) && (
              <Text type="danger">
                {!Object.values(currentAssignmentData?.rubric || {}).length
                  ? "*Create a rubric first."
                  : !isRubricHasPoints
                    ? "*Rubric must have points."
                    : ""}
              </Text>
            )}
          </Flex>
          <div style={{ marginBottom: 24 }}>
            <Text
              strong
              style={{ fontSize: 16, display: "block", marginBottom: 16 }}
            >
              Grading Preferences
            </Text>
            <Flex column gap="gap.medium" fill>
              <div>
                <Text style={{ display: "block", marginBottom: 8 }}>
                  Default Feedback Tone
                </Text>
                <Select
                  data-cy="feedback-tone-selector"
                  style={{ width: "100%" }}
                  defaultValue={assignmentSettings.feedbackTone}
                  onChange={(value) =>
                    setAssignmentSettings({
                      ...assignmentSettings,
                      feedbackTone: value,
                    })
                  }
                  disabled={!currentAssignmentData.aiGrading}
                >
                  <Option value="formal">Formal</Option>
                  <Option value="encouraging">Encouraging</Option>
                  <Option value="concise">Concise</Option>
                  <Option value="constructive">Constructive</Option>
                </Select>
              </div>

              <div>
                <Text style={{ display: "block", marginBottom: 8 }}>
                  Default Grade Scale
                </Text>
                <Select
                  data-cy="feedback-grade-scale-selector"
                  style={{ width: "100%" }}
                  defaultValue={assignmentSettings.gradeScale}
                  onChange={(value) =>
                    setAssignmentSettings({
                      ...assignmentSettings,
                      gradeScale: value,
                    })
                  }
                  disabled={!currentAssignmentData.aiGrading}
                >
                  <Option value="letter">Letter Grades (A-F)</Option>
                </Select>
              </div>

              <div>
                <Text style={{ display: "block", marginBottom: 8 }}>
                  Default Confidence Threshold
                </Text>
                <Slider
                  fluid
                  value={assignmentSettings?.confidenceThreshold || 0}
                  min={0}
                  max={100}
                  onChange={(value) =>
                    setAssignmentSettings({
                      ...assignmentSettings,
                      confidenceThreshold: value,
                    })
                  }
                  disabled={!currentAssignmentData.aiGrading}
                />
                <Flex gap="gap.smaller" space="between">
                  <Button
                    data-cy="all-suggestions-btn"
                    iconOnly
                    text
                    disabled={!currentAssignmentData.aiGrading}
                    content="Show all suggestions"
                    style={{ fontSize: 12 }}
                    onClick={() =>
                      setAssignmentSettings({
                        ...assignmentSettings,
                        confidenceThreshold: 20,
                      })
                    }
                  />
                  <Button
                    iconOnly
                    data-cy="high-confidence-btn"
                    text
                    disabled={!currentAssignmentData.aiGrading}
                    content="Only high confidence (70%)"
                    style={{ fontSize: 12 }}
                    onClick={() =>
                      setAssignmentSettings({
                        ...assignmentSettings,
                        confidenceThreshold: 50,
                      })
                    }
                  />
                  <Button
                    iconOnly
                    data-cy="very-high-confidence-btn"
                    text
                    disabled={!currentAssignmentData.aiGrading}
                    content="Only very high confidence"
                    style={{ fontSize: 12 }}
                    onClick={() =>
                      setAssignmentSettings({
                        ...assignmentSettings,
                        confidenceThreshold: 100,
                      })
                    }
                  />
                </Flex>
              </div>

              <div style={{ marginTop: 24 }}>
                <Text
                  strong
                  style={{ fontSize: 16, display: "block", marginBottom: 16 }}
                >
                  Privacy & Security
                </Text>

                <div>
                  <Text style={{ display: "block", marginBottom: 8 }}>
                    Data Retention
                  </Text>
                  <Select
                    data-cy="data-retention-btn"
                    style={{ width: "100%" }}
                    defaultValue={assignmentSettings.dataRetention}
                    onChange={(value) =>
                      setAssignmentSettings({
                        ...assignmentSettings,
                        dataRetention: value,
                      })
                    }
                    disabled={!currentAssignmentData.aiGrading}
                  >
                    <Option value="forever">Keep all data</Option>
                    <Option value="365">Auto-delete after 1 year</Option>
                    <Option value="120">Auto-delete after semester</Option>
                    <Option value="30">Auto-delete after 30 days</Option>
                  </Select>
                </div>
              </div>

              {/* <div>
                <Text style={{ display: "block", marginBottom: 8 }}>
                  Auto-Save Interval
                </Text>
                <Select style={{ width: "100%" }} defaultValue="2">
                  <Option value="1">Every 1 minute</Option>
                  <Option value="2">Every 2 minutes</Option>
                  <Option value="5">Every 5 minutes</Option>
                  <Option value="10">Every 10 minutes</Option>
                </Select>
              </div> */}
            </Flex>
          </div>
          <Flex hAlign="end" gap="gap.smaller">
            {/* <Button
              content="Cancel"
              disabled={!currentAssignmentData.aiGrading}
            /> */}
            <Button
              data-cy="setting-next-btn"
              primary
              iconPosition="after"
              content="Next"
              style={{ borderRadius: "8px" }}
              icon={<ChevronEndIcon />}
              // disabled={!currentAssignmentData.aiGrading}
              onClick={() => {
                setSelectedTab("form");
              }}
            />
          </Flex>
        </Col>

        {/* <Col span={12}>
          <div style={{ marginBottom: 24 }}>
            <Text
              strong
              style={{ fontSize: 16, display: "block", marginBottom: 16 }}
            >
              AI Training Preferences
            </Text>
            <Space direction="vertical" style={{ width: "100%" }} size="large">
              <Checkbox
                defaultChecked
                label={
                  <div>
                    <Text strong>Learn from my edits</Text>
                    <div>
                      <Text type="secondary">
                        Allow the AI to learn from your modifications to its
                        suggestions
                      </Text>
                    </div>
                  </div>
                }
              />

              <Checkbox
                defaultChecked
                label={
                  <div>
                    <Text strong>Use my historical grading patterns</Text>
                    <div>
                      <Text type="secondary">
                        Analyze your past grading to better match your style
                      </Text>
                    </div>
                  </div>
                }
              />

              <Checkbox
                label={
                  <div>
                    <Text strong>Share anonymized data to improve AI</Text>
                    <div>
                      <Text type="secondary">
                        Help improve the system for all educators (student data
                        never shared)
                      </Text>
                    </div>
                  </div>
                }
              />
            </Space>

            <div style={{ marginTop: 24 }}>
              <Text
                strong
                style={{ fontSize: 16, display: "block", marginBottom: 16 }}
              >
                Privacy & Security
              </Text>
              <Space
                direction="vertical"
                style={{ width: "100%" }}
                size="large"
              >
                <div>
                  <Text style={{ display: "block", marginBottom: 8 }}>
                    Data Retention
                  </Text>
                  <Select style={{ width: "100%" }} defaultValue="30">
                    <Option value="forever">Keep all data</Option>
                    <Option value="365">Auto-delete after 1 year</Option>
                    <Option value="120">Auto-delete after semester</Option>
                    <Option value="30">Auto-delete after 30 days</Option>
                  </Select>
                </div>

                <div>
                  <Text style={{ display: "block", marginBottom: 8 }}>
                    Two-Factor Authentication
                  </Text>
                  <Button icon={<ShieldOutlined />}>Enable 2FA</Button>
                </div>
              </Space>
            </div>
          </div>
        </Col> */}
      </Row>
      <ConfirmDialog
        style={{
          maxWidth: "450px",
        }}
        isConfirmDelete={isConfirmAiGrading}
        setisConfirmDelete={setIsConfirmAiGrading}
        onConfirm={handleConfirmAiGrading}
        onCancel={handleCancelAiGrading}
        header="Enable AI Grading"
        content="You will be charged for each assignment submission that uses AI grading. This feature will automatically grade assignments based on your rubric."
        confirmButton={{
          "data-cy": "enable-ai-grading",
          content: "Enable AI Grading",
        }}
        cancelButton="Cancel"
      />
    </div>
  );
};

export default AssignmentSettings;
